import {
  normalizeEvent,
  reduceProjectView,
  type ProjectViewState,
} from '@web/lib/projector';
import { RunDomainEventHub, runDomainEventHub } from './eventHub';
import { RunProjectionStore, runProjectionStore } from './projectionStore';
import {
  categoryForRunEvent,
  type RunDomainEvent,
  type RunEventDeliveryMode,
} from './types';

const REDACTED_KEY =
  /(?:authorization|cookie|password|secret|token|api[_-]?key)/i;

function safeHubValue(value: unknown, depth = 0): unknown {
  if (depth >= 6) return '[TRUNCATED]';
  if (typeof value === 'string') {
    return value.length > 4096 ? `${value.slice(0, 4096)}…` : value;
  }
  if (Array.isArray(value)) {
    return value.slice(0, 100).map((item) => safeHubValue(item, depth + 1));
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .slice(0, 200)
        .map(([key, item]) => [
          key,
          REDACTED_KEY.test(key) ? '[REDACTED]' : safeHubValue(item, depth + 1),
        ])
    );
  }
  return value;
}

export class RunEventIngress {
  private lastGapReason: string | null = null;

  constructor(
    private readonly store: RunProjectionStore = runProjectionStore,
    private readonly hub: RunDomainEventHub = runDomainEventHub
  ) {}

  apply(
    current: ProjectViewState,
    raw: unknown,
    deliveryMode: RunEventDeliveryMode
  ): ProjectViewState {
    const event = normalizeEvent(raw);
    if (event.projectId !== current.projectId) {
      throw new Error(
        `Run event project scope mismatch: expected ${current.projectId}, received ${event.projectId}`
      );
    }
    if (current.seenEventIds[event.eventId]) return current;
    const next = {
      ...reduceProjectView({ ...current, mode: 'live' }, event),
      lastSyncedAt: new Date().toISOString(),
    };
    this.store.replaceProject(next);
    const applied = Boolean(next.seenEventIds[event.eventId]);
    if (applied) {
      this.lastGapReason = null;
      const domainEvent: RunDomainEvent = {
        ...event,
        payload: safeHubValue(event.payload) as Record<string, unknown>,
        raw: undefined,
        schemaVersion: 1,
        deliveryMode,
        origin:
          deliveryMode === 'cloud_restore'
            ? 'cloud_restore'
            : event.origin || 'remote',
        category: categoryForRunEvent(event.eventType),
      };
      this.hub.publish(domainEvent);
    } else if (
      next.needsResync &&
      next.resyncReason &&
      next.resyncReason !== current.resyncReason &&
      next.resyncReason !== this.lastGapReason
    ) {
      this.lastGapReason = next.resyncReason;
      this.hub.publish({
        ...event,
        schemaVersion: 1,
        deliveryMode,
        origin: event.origin || 'remote',
        category: 'sync',
        eventId: `sync-gap:${event.eventId}:${next.resyncReason}`,
        eventType: 'run.sync.gap_detected',
        payload: { reason: next.resyncReason },
        raw: undefined,
      });
    }
    return next;
  }
}
